/**
 * Platform Insights filter state, kept in the URL so any view can be linked to,
 * bookmarked or sent to a colleague — including an open person or group.
 *
 * Defaults are omitted from the URL. Pure parse/serialize functions are
 * exported for tests; `useInsightsFilters` wires them to history.
 */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type View = "staff" | "dev";
export type Preset = "1d" | "7d" | "30d" | "90d" | "all";

export const PRESETS: { value: Preset; label: string; short: string }[] = [
  { value: "1d", label: "Last 24 hours", short: "24h" },
  { value: "7d", label: "Last 7 days", short: "7d" },
  { value: "30d", label: "Last 30 days", short: "30d" },
  { value: "90d", label: "Last 90 days", short: "90d" },
  { value: "all", label: "All time", short: "All" },
];

export const STAFF_TABS = ["overview", "people", "groups", "emails", "changes"] as const;
export const DEV_TABS = ["traffic", "sessions", "auth", "errors", "performance", "ai", "calls"] as const;
export type StaffTab = (typeof STAFF_TABS)[number];
export type DevTab = (typeof DEV_TABS)[number];
export type Tab = StaffTab | DevTab;

export interface InsightsFilters {
  view: View;
  tab: Tab;
  preset: Preset | null;      // null when a custom range is set
  from: string | null;        // YYYY-MM-DD, inclusive
  to: string | null;          // YYYY-MM-DD, inclusive
  compare: boolean;
  excludeInternal: boolean;
  includeAnonymous: boolean;
  person: string | null;      // open profile drawer (user id)
  group: string | null;       // open group drawer ("community:29")
}

export const VIEW_STORAGE_KEY = "insights.view";

export function defaultFilters(view: View = "staff"): InsightsFilters {
  return {
    view,
    tab: view === "dev" ? "traffic" : "overview",
    preset: "30d",
    from: null,
    to: null,
    compare: true,
    excludeInternal: true,
    // Developers see every request by default — anonymous 401s and public
    // pages are part of their picture. Staff numbers are about people.
    includeAnonymous: view === "dev",
    person: null,
    group: null,
  };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isPreset = (v: string | null): v is Preset => !!v && PRESETS.some((p) => p.value === v);
const isTab = (v: string | null, view: View): v is Tab =>
  !!v && (view === "dev" ? [...DEV_TABS, ...STAFF_TABS] : STAFF_TABS).includes(v as never);

/** The Developer view is for superadmins; everyone else always gets the staff view. */
export function canUseDeveloperView(role: string | null | undefined): boolean {
  return (role || "").toLowerCase() === "superadmin";
}

/**
 * URL → filters. Invalid values fall back to defaults rather than throwing.
 * `canDev` false pins the staff view, whatever a shared link or this browser asks for.
 */
export function parseFilters(search: string, storedView?: string | null, canDev = true): InsightsFilters {
  const q = new URLSearchParams(search);
  const urlView = q.get("view");
  const asked: View = urlView === "dev" || urlView === "staff"
    ? urlView
    : storedView === "dev" ? "dev" : "staff";
  const view: View = canDev ? asked : "staff";
  const d = defaultFilters(view);

  const from = q.get("from");
  const to = q.get("to");
  const custom = from && ISO_DATE.test(from) ? { from, to: to && ISO_DATE.test(to) && to >= from ? to : null } : null;
  const range = q.get("range");
  const bool = (key: string, fallback: boolean) => {
    const v = q.get(key);
    return v === "1" ? true : v === "0" ? false : fallback;
  };
  const tab = q.get("tab");
  const group = q.get("group");

  return {
    view,
    tab: isTab(tab, view) ? tab : d.tab,
    preset: custom ? null : isPreset(range) ? range : d.preset,
    from: custom?.from ?? null,
    to: custom?.to ?? null,
    compare: bool("compare", d.compare),
    excludeInternal: bool("internal", d.excludeInternal),
    includeAnonymous: bool("anon", d.includeAnonymous),
    person: q.get("person") || null,
    group: group && /^[a-z]+:\d+$/.test(group) ? group : null,
  };
}

const OWN_KEYS = ["view", "tab", "range", "from", "to", "compare", "internal", "anon", "person", "group"];

/** Filters → URL search string, keeping unrelated params (e.g. `section`). */
export function serializeFilters(f: InsightsFilters, base = ""): string {
  const q = new URLSearchParams(base);
  OWN_KEYS.forEach((k) => q.delete(k));
  const d = defaultFilters(f.view);
  if (f.view !== "staff") q.set("view", f.view);
  if (f.tab !== d.tab) q.set("tab", f.tab);
  if (f.from) {
    q.set("from", f.from);
    if (f.to) q.set("to", f.to);
  } else if (f.preset && f.preset !== d.preset) {
    q.set("range", f.preset);
  }
  if (f.compare !== d.compare) q.set("compare", f.compare ? "1" : "0");
  if (f.excludeInternal !== d.excludeInternal) q.set("internal", f.excludeInternal ? "1" : "0");
  if (f.includeAnonymous !== d.includeAnonymous) q.set("anon", f.includeAnonymous ? "1" : "0");
  if (f.person) q.set("person", f.person);
  if (f.group) q.set("group", f.group);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Filters → API query params for the shared backend window. */
export function windowParams(f: InsightsFilters, tz = browserTimeZone()): Record<string, string> {
  const p: Record<string, string> = { tz };
  if (f.from) {
    p.start = f.from;
    if (f.to) p.end = f.to;
  } else {
    p.range = f.preset ?? "30d";
  }
  if (f.compare) p.compare = "true";
  p.exclude_internal = String(f.excludeInternal);
  p.include_anonymous = String(f.includeAnonymous);
  return p;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function readStoredView(): string | null {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Apply a filter change, resolving the fields that depend on each other. */
export function applyPatch(prev: InsightsFilters, patch: Partial<InsightsFilters>): InsightsFilters {
  let next = { ...prev, ...patch };
  if (patch.view && patch.view !== prev.view) {
    // Switching view resets the tab and the view-dependent default.
    const d = defaultFilters(patch.view);
    next = { ...next, tab: patch.tab ?? d.tab, includeAnonymous: d.includeAnonymous };
  }
  if (patch.preset) next = { ...next, from: null, to: null };
  if (patch.from) next = { ...next, preset: null };
  return next;
}

/**
 * The live filter state. Changing filters replaces the history entry; opening
 * a person or group pushes one, so the browser's Back button closes the drawer.
 */
export function useInsightsFilters(canDev: boolean) {
  const [filters, setFilters] = useState<InsightsFilters>(() =>
    typeof window === "undefined" ? defaultFilters() : parseFilters(window.location.search, readStoredView(), canDev),
  );

  useEffect(() => {
    const onPop = () => setFilters(parseFilters(window.location.search, readStoredView(), canDev));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [canDev]);

  // The latest state, for building the next one outside a state updater:
  // writing history inside an updater runs during render, and Next's router
  // listens to history — React then warns about updating Router mid-render.
  const latest = useRef(filters);
  useEffect(() => { latest.current = filters; }, [filters]);

  const update = useCallback((patch: Partial<InsightsFilters>, mode: "replace" | "push" = "replace") => {
    if (patch.view === "dev" && !canDev) patch = { ...patch, view: "staff" };
    const next = applyPatch(latest.current, patch);
    if (patch.view && patch.view !== latest.current.view) {
      try { window.localStorage.setItem(VIEW_STORAGE_KEY, patch.view); } catch { /* private mode */ }
    }
    latest.current = next;
    setFilters(next);
    const url = `${window.location.pathname}${serializeFilters(next, window.location.search)}`;
    if (mode === "push") window.history.pushState(null, "", url);
    else window.history.replaceState(null, "", url);
  }, [canDev]);

  // The role can arrive after first render (or change on an identity swap):
  // without developer access the staff view is shown whatever the state says.
  const effective = canDev || filters.view === "staff"
    ? filters
    : { ...filters, view: "staff" as const, tab: (STAFF_TABS as readonly string[]).includes(filters.tab) ? filters.tab : "overview" as Tab,
        includeAnonymous: false };

  return { filters: effective, update };
}
